import React, { useEffect, useRef } from 'react'
import cn from 'classnames'
import s from './styles/StoreList.module.scss'
import NoResult from '@components/icons/NoResult'
import Direction from '@components/icons/Direction'
import Website from '@components/icons/Website'
import { ChevronDown, ChevronUp } from 'react-feather'
import { useTranslation } from 'next-i18next'
import { getStoreCode } from 'config'

export interface Store {
    storelocator_id: number
    storename: string
    address: string
    city: string
    region: string
    zipcode: string
    store_tag: string
    storetime?: string
    telephone: string
    website_url?: string
    google_map_link?: string
    latitude: string
    longitude: string
    is_active: boolean
}

interface Timing {
    days: string
    day_status: '0' | '1'
    open_hour: string
    open_minute: string
    close_hour: string
    close_minute: string
}

export const StoreList: React.FC<{
    activeStore: number | null
    setActiveStore: (storeId: number) => void
    storeList: Store[]
    expandedStoreId: number | null
    setExpandedStoreId: (storeId: number | null) => void
    filteredStores: Store[]
    currentPosition: google.maps.LatLngLiteral | null
}> = ({
    activeStore,
    setActiveStore,
    storeList,
    expandedStoreId,
    setExpandedStoreId,
    filteredStores,
    currentPosition
}) => {
    const { t } = useTranslation('common')
    const storeCode = getStoreCode()

    const storeListContainerRef = useRef<HTMLDivElement>(null)
    const isArabicStore =
        storeCode === 'ooka_uae_store_en' || storeCode === 'ooka_uae_store_ar'

    const scrollToElm = (
        container: HTMLElement,
        elm: HTMLElement,
        duration: number
    ): void => {
        const pos = getRelativePos(elm)
        scrollTo(container, pos.top, duration / 1000) // duration in seconds
    }

    const getRelativePos = (elm: HTMLElement): { top: number } => {
        const parentPos = elm.parentElement!.getBoundingClientRect()
        const elemPos = elm.getBoundingClientRect()
        return {
            top: elemPos.top - parentPos.top + elm.parentElement!.scrollTop
        }
    }

    const scrollTo = (
        element: HTMLElement,
        to: number,
        duration: number,
        onDone?: () => void
    ): void => {
        const start = element.scrollTop
        const change = to - start
        const startTime = performance.now()

        const animateScroll = () => {
            const now = performance.now()
            const elapsed = (now - startTime) / 1000
            const t = Math.min(elapsed / duration, 1)

            element.scrollTop = start + change * easeInOutQuad(t)

            if (t < 1) {
                window.requestAnimationFrame(animateScroll)
            } else if (onDone) {
                onDone()
            }
        }

        animateScroll()
    }

    const easeInOutQuad = (t: number): number => {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
    }

    const calculateDistance = (
        lat1: number,
        lon1: number,
        lat2: number,
        lon2: number
    ) => {
        const earthRadiusKm = 6371 // Radius of the earth in km
        const deltaLat = degreesToRadians(lat2 - lat1)
        const deltaLon = degreesToRadians(lon2 - lon1)
        const angleA =
            Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(degreesToRadians(lat1)) *
                Math.cos(degreesToRadians(lat2)) *
                Math.sin(deltaLon / 2) *
                Math.sin(deltaLon / 2)
        const centralAngle =
            2 * Math.atan2(Math.sqrt(angleA), Math.sqrt(1 - angleA))
        const distance = earthRadiusKm * centralAngle // Distance in km
        return distance
    }

    const degreesToRadians = (degrees: number) => {
        return degrees * (Math.PI / 180)
    }

    const sortByDistance = (storeA: Store, storeB: Store) => {
        const compareStore = {
            lat: Number(
                currentPosition?.lat ?? filteredStores[0]?.latitude ?? 0
            ),
            lng: Number(
                currentPosition?.lng ?? filteredStores[0]?.longitude ?? 0
            )
        }
        const distanceToA = calculateDistance(
            parseFloat(storeA.latitude),
            parseFloat(storeA.longitude),
            compareStore.lat,
            compareStore.lng
        )
        const distanceToB = calculateDistance(
            parseFloat(storeB.latitude),
            parseFloat(storeB.longitude),
            compareStore.lat,
            compareStore.lng
        )
        return distanceToA - distanceToB
    }

    const sortedStoreList =
        filteredStores.length > 0
            ? storeList?.slice().sort(sortByDistance)
            : storeList

    useEffect(() => {
        if (expandedStoreId !== null && storeListContainerRef.current) {
            const activeStoreIndex = sortedStoreList.findIndex(
                (store: Store) => store.storelocator_id === expandedStoreId
            )
            if (activeStoreIndex !== -1) {
                const storeCard = storeListContainerRef.current.children[
                    activeStoreIndex
                ] as HTMLElement

                scrollToElm(storeListContainerRef.current, storeCard, 600)
            }
        }
    }, [expandedStoreId])

    const handleToggleExpansion = (storeId: number) => {
        setExpandedStoreId(storeId === expandedStoreId ? null : storeId)
        setActiveStore(storeId)
    }

    const handleStoreSelect = (storeId: number) => {
        handleToggleExpansion(storeId)
        setActiveStore(storeId)
    }

    const parseStoreTimings = (storeTimeString: string) => {
        try {
            const timings: Timing[] = JSON.parse(storeTimeString)
            return timings.map((timing) => {
                const day = timing.days
                const isOpen = timing.day_status === '1'
                const opening = `${timing.open_hour}:${timing.open_minute}`
                const closing = `${timing.close_hour}:${timing.close_minute}`
                return (
                    <p
                        key={day}
                        className={cn(
                            s['store-timings__day'],
                            isOpen ? s['open'] : s['closed']
                        )}
                    >
                        <span>{t(`storeLocator|${day}`)}</span>
                        <span>{isOpen && ` ${opening} - ${closing}`}</span>
                        <br />
                    </p>
                )
            })
        } catch (error) {
            console.error('Error parsing store timings:', error)
            return null
        }
    }

    const renderNoResults = () => {
        if (storeList?.length) return null

        return (
            <div className={cn(s['no-result'])}>
                <NoResult />
                <p>{t('storeLocator|No Results in this area')}</p>
                <p>
                    {t(
                        'storeLocator|We are sorry but we could not find a store within a radius of 100 miles of your entered location.'
                    )}
                </p>
            </div>
        )
    }

    return (
        <div
            ref={storeListContainerRef}
            className={cn(s['storelist-container'])}
        >
            {renderNoResults()}
            {sortedStoreList.map((store, index) => {
                const storeId = store.storelocator_id
                const website = store?.website_url
                const city = store?.city
                const region = store?.region
                const zipcode = isArabicStore ? '' : store?.zipcode
                const cleanAddress = store.address.replace(/\\n/g, '')
                const fullAddress = [cleanAddress, city, region, zipcode]
                    .filter(Boolean)
                    .join(', ')
                return (
                    <div
                        key={storeId}
                        className={cn(s['store-card'], {
                            [s.active]: activeStore === storeId
                        })}
                    >
                        <div
                            className={cn(s['store-header'])}
                            role="button"
                            tabIndex={0}
                            onKeyDown={() => handleStoreSelect(storeId)}
                            onClick={() => handleStoreSelect(storeId)}
                        >
                            <div className={cn(s['store-details'])}>
                                <h4>
                                    <span className={cn(s['store-count'])}>
                                        {index + 1} .
                                    </span>
                                    <span> {store.storename} </span>
                                </h4>
                                <p>{fullAddress}</p>
                                <p className={cn(s['store-type'])}>
                                    <span>{store.store_tag}</span>
                                </p>
                            </div>
                            <div
                                className={cn(s['expanded-details'])}
                                role="button"
                                tabIndex={0}
                            >
                                {expandedStoreId &&
                                expandedStoreId === storeId ? (
                                    <ChevronUp />
                                ) : (
                                    <ChevronDown />
                                )}
                            </div>
                        </div>
                        {storeId === expandedStoreId && (
                            <div className={s['store-details-expanded']}>
                                {store.storetime?.length && (
                                    <div className={cn(s['store-timings'])}>
                                        <h5>
                                            {t('storeLocator|Opening hours')}
                                        </h5>
                                        {parseStoreTimings(store.storetime)}
                                    </div>
                                )}
                                <div className={cn(s['store-contact'])}>
                                    <h5>{t('storeLocator|Contact')}</h5>
                                    <p className={cn(s['contact-info'])}>
                                        <span>{t('storeLocator|Phone')}</span>
                                        <span>{store.telephone}</span>
                                    </p>
                                </div>
                                {website && store?.google_map_link && (
                                    <div className={cn(s['store-btns'])}>
                                        <a
                                            href={store?.google_map_link}
                                            className={cn(
                                                s['btn-secondary'],
                                                'btn-secondary'
                                            )}
                                        >
                                            <Direction />{' '}
                                            {t('storeLocator|Direction')}
                                        </a>
                                        <a
                                            href={website}
                                            className={cn(
                                                s['btn-secondary'],
                                                'btn-secondary'
                                            )}
                                        >
                                            <Website />{' '}
                                            {t('storeLocator|Website')}
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default StoreList
